import { debounce } from 'lodash'
import { get } from 'utils/lodash'
import { createAsyncThunk } from '@reduxjs/toolkit'
import { parseBatchForDraftPayload } from 'containers/billing/payment-batches/utils'
import api from 'api' // Import the base API
import { handleError } from 'utils/error'
import { createDataGridSlice } from '../generic/createDataGridSlice'

const getInitialState = () => ({
  rows: [],
  sorting: {
    sortModel: [{ field: 'createdAt', sort: 'desc' }],
  },
  selectionModel: [],
  selectedRows: [],
  filters: {
    filterModel: { items: [], quickFilterExcludeHiddenColumns: false },
    allFilters: [],
    searchFilters: {},
    sortRules: [{ columnName: 'createdAt', operation: 'DESC' }],
    queryParams: {},
  },
  addToGroup: {
    isOpen: false,
    batchIds: [],
  },
  postingBatchIds: [], // Track which batches are currently being posted
})

// Debounced async thunk for auto-saving
const debouncedSaveToServer = debounce(async (dispatch, batch, getState) => {
  try {
    // Double-check that the batch is not being posted right before saving
    const currentState = getState()
    const isCurrentlyPosting = currentState.paymentBatches.postingBatchIds.includes(batch.id)

    if (isCurrentlyPosting) {
      return
    }

    const parsedBatch = parseBatchForDraftPayload(batch, batch.source)
    // Use the base API with the endpoint name
    await dispatch(api.endpoints.createUpdateDraftBatch.initiate(parsedBatch))
  } catch (error) {
    handleError(error)
  }
}, 1000)

// Enhanced action that updates state and auto-saves
export const updatePaymentBatchWithAutoSave = createAsyncThunk(
  'paymentBatches/updateWithAutoSave',
  async ({ id, changes }, { dispatch, getState }) => {
    // Update the state first
    dispatch(updatePaymentBatch({ id, changes }))

    // Get the updated batch and check if it's currently being posted
    const state = getState()
    const batch = state.paymentBatches.rows.find(b => b.id === id)
    const isCurrentlyPosting = state.paymentBatches.postingBatchIds.includes(id)

    // Only auto-save if the batch exists and is not currently being posted
    if (batch && !isCurrentlyPosting) {
      debouncedSaveToServer(dispatch, batch, getState)
    }

    return { id, changes }
  }
)

export const paymentBatches = createDataGridSlice({
  name: 'paymentBatches',
  initialState: getInitialState(),
  reducers: {
    setPaymentBatchesSelectionModel: (state, action) => {
      const selectionModel = get(action, 'payload.selectionModel', [])
      const rows = get(action, 'payload.rows', [])
      state.selectionModel = selectionModel
      if (selectionModel.length > 0) {
        const selectedSet = new Set(selectionModel)
        state.selectedRows = rows.filter(row => selectedSet.has(row.id))
      } else {
        state.selectedRows = []
      }
    },
    setPaymentBatchesList: (state, action) => {
      state.rows = action.payload
    },
    addPaymentBatch: (state, action) => {
      const newBatch = action.payload
      if (!state.rows.find(batch => batch.id === newBatch.id)) {
        state.rows.push(newBatch)
      }
    },
    updatePaymentBatch: (state, action) => {
      const { id, changes } = action.payload
      const index = state.rows.findIndex(batch => batch.id === id)
      if (index > -1) {
        state.rows[index] = { ...state.rows[index], ...changes }
      } else {
        state.rows.push({ id, ...changes })
      }
    },
    addPayment: (state, action) => {
      const { batchId, payment } = action.payload
      const batchIndex = state.rows.findIndex(batch => batch.id === batchId)
      if (batchIndex > -1) {
        const payments = state.rows[batchIndex].payments || []
        if (!payments.find(p => p.id === payment.id)) {
          payments.push(payment)
          state.rows[batchIndex].payments = payments
        }
      }
    },
    addPayments: (state, action) => {
      const { batchId, payments } = action.payload
      const batchIndex = state.rows.findIndex(batch => batch.id === batchId)
      if (batchIndex > -1) {
        const existing = state.rows[batchIndex].payments || []
        const merged = [...existing]
        payments.forEach(payment => {
          if (!merged.find(p => p.id === payment.id)) {
            merged.push(payment)
          }
        })
        state.rows[batchIndex].payments = merged
      }
    },
    updatePayment: (state, action) => {
      const { batchId, paymentId, changes } = action.payload
      const batchIndex = state.rows.findIndex(batch => batch.id === batchId)
      if (batchIndex > -1) {
        const payments = state.rows[batchIndex].payments || []
        const paymentIndex = payments.findIndex(payment => payment.id === paymentId)
        if (paymentIndex > -1) {
          payments[paymentIndex] = { ...payments[paymentIndex], ...changes }
          state.rows[batchIndex].payments = payments
        }
      }
    },
    removePaymentBatch: (state, action) => {
      const id = action.payload
      state.rows = state.rows.filter(batch => batch.id !== id)
    },
    setAddToGroupDialogState: (state, action) => {
      state.addToGroup = action.payload
    },
    setPostingBatch: (state, action) => {
      const { id, isPosting } = action.payload
      if (isPosting) {
        if (!state.postingBatchIds.includes(id)) {
          state.postingBatchIds.push(id)
        }
      } else {
        state.postingBatchIds = state.postingBatchIds.filter(batchId => batchId !== id)
      }
    },
  },
  extraReducers: builder => {
    builder.addCase(updatePaymentBatchWithAutoSave.fulfilled, (state, action) => {
      // State already updated by the regular updatePaymentBatch action
      console.log('Batch auto-saved:', action.payload.id)
    })
  },
})

export const {
  setSortModel: setPaymentBatchesSortModel,
  setFilters: setPaymentBatchesFilters,
  setPaymentBatchesSelectionModel,
  setPaymentBatchesList,
  addPaymentBatch,
  updatePaymentBatch,
  addPayment,
  addPayments,
  updatePayment,
  removePaymentBatch,
  setAddToGroupDialogState,
  setPostingBatch,
} = paymentBatches.actions

export default paymentBatches.reducer
